import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChefHat, Plus, Edit, Trash2, FolderPlus, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { NumberInput } from '@/components/ui/NumberInput';
import {
  createCategory, updateCategory, deleteCategory, listCategories,
  createDish, updateDish, deleteDish, listDishes,
  createModifier, updateModifier, deleteModifier, listModifiers,
  createSection, updateSection, deleteSection, listSections,
  listTechCards,
  type Category, type Dish, type Modifier,
} from '@/services/admin';
import { formatRub } from '@/utils/format';
import { asAbsoluteUrl } from '@/utils/url';
import { PhotosField } from '@/components/PhotosField';
import { ModifierTechCardModal, TechCardEditor } from '@/components/techcard/TechCardEditor';
import MenuScannerTab from '@/components/MenuScannerTab';
import MenuImportTab from '@/components/MenuImportTab';

type Tab = 'categories' | 'sections' | 'dishes' | 'modifiers' | 'scanner' | 'csv';
const TABS: Tab[] = ['categories', 'sections', 'dishes', 'modifiers', 'scanner', 'csv'];

export default function MenuPage() {
  const [params, setParams] = useSearchParams();
  const initialTab = params.get('tab') as Tab | null;
  const [tab, setTabState] = useState<Tab>(
    initialTab && TABS.includes(initialTab) ? initialTab : 'dishes',
  );

  // Держим выбранный таб в URL — чтобы работали deep-links из онбординга
  // и сохранялся выбор при перезагрузке.
  const setTab = (t: Tab) => {
    setTabState(t);
    setParams(
      (prev) => {
        prev.set('tab', t);
        return prev;
      },
      { replace: true },
    );
  };

  return (
    <div>
      <header className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold">Меню</h1>
          <p className="text-sm text-slate-500">Категории, блюда, модификаторы</p>
        </div>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg overflow-x-auto no-scrollbar w-full sm:w-auto">
          <TabBtn active={tab === 'categories'} onClick={() => setTab('categories')}>Категории</TabBtn>
          <TabBtn active={tab === 'sections'} onClick={() => setTab('sections')}>Разделы</TabBtn>
          <TabBtn active={tab === 'dishes'} onClick={() => setTab('dishes')}>Блюда</TabBtn>
          <TabBtn active={tab === 'modifiers'} onClick={() => setTab('modifiers')}>Модификаторы</TabBtn>
          <TabBtn active={tab === 'scanner'} onClick={() => setTab('scanner')}>Сканер</TabBtn>
          <TabBtn active={tab === 'csv'} onClick={() => setTab('csv')}>Импорт</TabBtn>
        </div>
      </header>

      {tab === 'categories' && <CategoriesTab />}
      {tab === 'sections' && <SectionsTab />}
      {tab === 'dishes' && <DishesTab />}
      {tab === 'modifiers' && <ModifiersTab />}
      {tab === 'scanner' && <MenuScannerTab />}
      {tab === 'csv' && <MenuImportTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap shrink-0 ${active ? 'bg-white shadow text-brand-700' : 'text-slate-600'}`}
    >
      {children}
    </button>
  );
}

// ─── Categories ────────────────────────────
function CategoriesTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState<{
    id?: string;
    name: string;
    sortOrder: number;
    isActive: boolean;
    visibleInMenu: boolean;
    discountPercent: number;
  } | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['admin/cat'], queryFn: listCategories });

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!form) return;
      if (form.id) return updateCategory(form.id, form);
      return createCategory(form);
    },
    onSuccess: () => {
      toast.success('Сохранено');
      setForm(null);
      qc.invalidateQueries({ queryKey: ['admin/cat'] });
      // Скидка категории меняет цены в меню касс/QR — сбрасываем кэш блюд.
      qc.invalidateQueries({ queryKey: ['admin/dishes'] });
    },
  });

  const delMut = useMutation({
    mutationFn: deleteCategory,
    // Причину отказа (в категории есть блюда) показывает перехватчик api.ts —
    // сервер отдаёт её текстом в 409, дублировать тост здесь не нужно.
    onSuccess: (res) => {
      const moved = (res as { movedArchivedDishes?: number } | undefined)?.movedArchivedDishes ?? 0;
      toast.success(
        moved > 0
          ? `Категория удалена. ${moved} архивных блюд из истории заказов перенесены в «Архив».`
          : 'Категория удалена',
      );
      qc.invalidateQueries({ queryKey: ['admin/cat'] });
      qc.invalidateQueries({ queryKey: ['admin/dishes'] });
    },
  });

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button className="btn-primary" onClick={() => setForm({ name: '', sortOrder: (data?.length ?? 0), isActive: true, visibleInMenu: true, discountPercent: 0 })}>
          <FolderPlus size={16} /> Категория
        </button>
      </div>
      <div className="card overflow-x-auto">
        {isLoading ? <Spinner /> : (
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Название</th>
                <th className="text-left px-4 py-3">Порядок</th>
                <th className="text-left px-4 py-3">Скидка</th>
                <th className="text-left px-4 py-3">Статус</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data?.map((c: Category) => (
                <tr key={c.id} className="table-row">
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3">{c.sortOrder}</td>
                  <td className="px-4 py-3">
                    {(c.discountPercent ?? 0) > 0 ? (
                      <Badge tone="warning">−{c.discountPercent}%</Badge>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {c.isActive ? <Badge tone="success">Активна</Badge> : <Badge tone="slate">Выкл</Badge>}
                      {c.isActive && c.visibleInMenu === false && <Badge tone="warning">Скрыта в меню</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button className="btn-ghost" onClick={() => setForm({ id: c.id, name: c.name, sortOrder: c.sortOrder, isActive: c.isActive, visibleInMenu: c.visibleInMenu ?? true, discountPercent: c.discountPercent ?? 0 })}>
                      <Edit size={16} />
                    </button>
                    <button className="btn-ghost text-red-600" onClick={() => {
                      if (confirm('Удалить категорию?')) delMut.mutate(c.id);
                    }}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {data?.length === 0 && (
                <tr><td colSpan={5} className="text-center text-slate-400 py-8">Категорий нет</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? 'Редактировать категорию' : 'Новая категория'}
        footer={
          <>
            <button className="btn-outline" onClick={() => setForm(null)}>Отмена</button>
            <button className="btn-primary" onClick={() => saveMut.mutate()}>Сохранить</button>
          </>
        }
      >
        {form && (
          <div className="grid gap-3">
            <div>
              <label className="label">Название</label>
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Порядок</label>
                <NumberInput className="input" decimal={false} value={form.sortOrder}
                  onChange={(n) => setForm({ ...form, sortOrder: n })} />
              </div>
              <div className="flex flex-col justify-end gap-2 pb-1">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                  <span className="text-sm">Активна</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.visibleInMenu}
                    onChange={(e) => setForm({ ...form, visibleInMenu: e.target.checked })} />
                  <span className="text-sm">Активна в электронном меню</span>
                </label>
              </div>
            </div>
            <p className="text-xs text-slate-400 -mt-1">
              «Активна в электронном меню» отключает категорию только в QR-меню для гостей. В кассе и админке она остаётся доступной.
            </p>
            <div>
              <label className="label">Скидка на всю категорию, %</label>
              <NumberInput
                className="input"
                decimal={false}
                value={form.discountPercent}
                zeroAsPlaceholder
                placeholder="0"
                onChange={(n) => setForm({ ...form, discountPercent: Math.max(0, Math.min(100, Math.round(n))) })}
              />
              <p className="text-xs text-slate-400 mt-1">
                Применяется к цене каждого блюда категории (поверх акционной цены блюда). Кассы и QR-меню
                показывают уже сниженную цену; в чеке позиция идёт по сниженной цене без отдельной строки «скидка».
                0 — скидки нет.
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── Sections (разделы приготовления) ──────
// Раздел определяет, на какой принтер уйдёт блюдо: касса печатает кухонный лист
// для «Кухни», барный — для «Бара» и так далее. Названия любые.

function SectionsTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState<{
    id?: string;
    name: string;
    sortOrder: number;
    isActive: boolean;
    legacyTag: 'KITCHEN' | 'BAR' | 'NONE' | '';
  } | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['admin/sections'], queryFn: listSections });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin/sections'] });
    qc.invalidateQueries({ queryKey: ['admin/dishes'] });
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!form) return;
      const payload = {
        name: form.name.trim(),
        sortOrder: form.sortOrder,
        isActive: form.isActive,
        legacyTag: form.legacyTag === '' ? null : form.legacyTag,
      };
      if (form.id) return updateSection(form.id, payload);
      return createSection(payload);
    },
    onSuccess: () => {
      toast.success('Сохранено');
      setForm(null);
      invalidate();
    },
  });

  const delMut = useMutation({
    mutationFn: deleteSection,
    onSuccess: (res) => {
      const moved = res?.movedDishes ?? 0;
      toast.success(
        moved > 0
          ? `Раздел удалён, ${moved} блюд перенесены в «Без раздела»`
          : 'Раздел удалён',
      );
      invalidate();
    },
  });

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          Раздел решает, на каком принтере печатать блюдо: кухня, бар, кальянная, цех.
          Кассы печатают каждый раздел на свой принтер, если он настроен.
        </p>
        <button
          className="btn-primary"
          onClick={() =>
            setForm({
              name: '',
              sortOrder: data?.length ?? 0,
              isActive: true,
              legacyTag: '',
            })
          }
        >
          <FolderPlus size={16} /> Раздел
        </button>
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? <Spinner /> : (
          <table className="w-full min-w-[620px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Название</th>
                <th className="text-left px-4 py-3">Блюд</th>
                <th className="text-left px-4 py-3">Порядок</th>
                <th className="text-left px-4 py-3">Старые кассы</th>
                <th className="text-left px-4 py-3">Статус</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data?.map((s) => (
                <tr key={s.id} className="table-row">
                  <td className="px-4 py-3 font-medium">{s.name}</td>
                  <td className="px-4 py-3 text-slate-500">{s._count?.dishes ?? 0}</td>
                  <td className="px-4 py-3">{s.sortOrder}</td>
                  <td className="px-4 py-3">
                    {s.legacyTag === 'KITCHEN' && <Badge tone="info">Кухня</Badge>}
                    {s.legacyTag === 'BAR' && <Badge tone="warning">Бар</Badge>}
                    {(s.legacyTag === 'NONE' || s.legacyTag === null) && (
                      <span className="text-xs text-slate-400">
                        {s.legacyTag === 'NONE' ? 'без раздела' : '—'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {s.isActive ? <Badge tone="success">Активен</Badge> : <Badge tone="slate">Выкл</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      className="btn-ghost"
                      onClick={() =>
                        setForm({
                          id: s.id,
                          name: s.name,
                          sortOrder: s.sortOrder,
                          isActive: s.isActive,
                          legacyTag: s.legacyTag ?? '',
                        })
                      }
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="btn-ghost text-red-600"
                      onClick={() => {
                        const n = s._count?.dishes ?? 0;
                        const msg = n > 0
                          ? `Удалить раздел «${s.name}»? ${n} блюд перейдут в «Без раздела».`
                          : `Удалить раздел «${s.name}»?`;
                        if (confirm(msg)) delMut.mutate(s.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {data?.length === 0 && (
                <tr><td colSpan={6} className="text-center text-slate-400 py-8">Разделов нет</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? 'Редактировать раздел' : 'Новый раздел'}
        footer={
          <>
            <button className="btn-outline" onClick={() => setForm(null)}>Отмена</button>
            <button className="btn-primary" onClick={() => saveMut.mutate()}>Сохранить</button>
          </>
        }
      >
        {form && (
          <div className="grid gap-3">
            <div>
              <label className="label">Название</label>
              <input
                className="input"
                placeholder="Например: Кальянная"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label">Порядок</label>
                <NumberInput className="input" decimal={false} value={form.sortOrder}
                  onChange={(n) => setForm({ ...form, sortOrder: n })} />
              </div>
              <div>
                <label className="label">Для старых касс</label>
                <select
                  className="input"
                  value={form.legacyTag}
                  onChange={(e) =>
                    setForm({ ...form, legacyTag: e.target.value as typeof form.legacyTag })
                  }
                >
                  <option value="">— не сопоставлять</option>
                  <option value="KITCHEN">Кухня</option>
                  <option value="BAR">Бар</option>
                  <option value="NONE">Без раздела</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              Активен
            </label>
            <p className="text-xs text-slate-400">
              «Для старых касс» нужно, пока не все планшеты обновлены: они умеют только
              «Кухня» и «Бар». Новым кассам приходит сам раздел, и печать идёт по нему.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── Dishes ────────────────────────────────
const MAX_PHOTOS = 10;

type DishForm = {
  id?: string;
  categoryId: string;
  /** Раздел приготовления из справочника; '' — «Без раздела». */
  sectionId: string;
  name: string;
  description: string;
  priceRub: number;
  // Пустая строка = закупочная цена не задана (тогда себестоимость берётся из
  // тех-карты). Держим строкой, чтобы отличать «0 ₽» от «не заполнено».
  costRub: string;
  isAvailable: boolean;
  photos: string[];
  modifierIds: string[];
};

// Маржа по блюду: сколько остаётся от цены продажи после закупки.
function marginPct(price: number, cost: number | null): number | null {
  if (cost == null || price <= 0) return null;
  return Math.round(((price - cost) / price) * 100);
}

function DishesTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState<DishForm | null>(null);
  const [categoryId, setCategoryId] = useState('');
  const [q, setQ] = useState('');

  const { data: categories = [] } = useQuery({ queryKey: ['admin/cat'], queryFn: listCategories });
  const { data: dishes, isLoading } = useQuery({
    queryKey: ['admin/dishes', { categoryId, q }],
    queryFn: () => listDishes({ categoryId: categoryId || undefined, q: q || undefined }),
  });
  const { data: mods = [] } = useQuery({ queryKey: ['admin/mods'], queryFn: listModifiers });
  const { data: sections = [] } = useQuery({ queryKey: ['admin/sections'], queryFn: listSections });
  // Тех-карты нужны прямо в списке блюд: без этого владелец не видит, что
  // себестоимость можно собрать из склада, и вбивает её руками.
  const { data: techCards = [] } = useQuery({
    queryKey: ['admin/stock/tech-cards'],
    queryFn: listTechCards,
  });
  const [techCardFor, setTechCardFor] = useState<Dish | null>(null);

  const techCardCostByDish = useMemo(() => {
    const map = new Map<string, number>();
    for (const tc of techCards) {
      const line = Math.round(tc.quantity * (tc.stockItem?.costPerUnit ?? 0));
      map.set(tc.dishId, (map.get(tc.dishId) ?? 0) + line);
    }
    return map;
  }, [techCards]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!form) return;
      const cost = form.costRub.trim();
      const payload = {
        categoryId: form.categoryId,
        name: form.name,
        description: form.description,
        price: Math.round(form.priceRub * 100),
        costPrice: cost === '' ? null : Math.round(Number(cost.replace(',', '.')) * 100),
        // Раздел шлём id'шником; бэкенд сам продублирует его в legacy-поле для
        // касс, которые ещё не обновились.
        sectionId: form.sectionId || null,
        isAvailable: form.isAvailable,
        photos: form.photos,
        modifierIds: form.modifierIds,
      };
      if (form.id) return updateDish(form.id, payload);
      return createDish(payload);
    },
    onSuccess: () => {
      toast.success('Сохранено');
      setForm(null);
      qc.invalidateQueries({ queryKey: ['admin/dishes'] });
    },
  });

  const delMut = useMutation({
    mutationFn: deleteDish,
    onSuccess: (res) => {
      // Блюдо, которое продавалось, физически не удаляется — уходит в архив
      // (история чеков и отчёты должны остаться целыми). Говорим об этом прямо.
      const r = res as { archived?: boolean; message?: string } | undefined;
      if (r?.archived) toast.success(r.message ?? 'Блюдо убрано из меню');
      else toast.success('Блюдо удалено');
      qc.invalidateQueries({ queryKey: ['admin/dishes'] });
    },
  });

  const openNew = () => setForm({
    categoryId: categories[0]?.id ?? '',
    // По умолчанию — «Кухня», если такой раздел есть: так блюдо сразу попадает
    // на кухонный принтер, а не остаётся без маршрута печати.
    sectionId: sections.find((s) => s.legacyTag === 'KITCHEN')?.id ?? sections[0]?.id ?? '',
    name: '', description: '', priceRub: 0, costRub: '', isAvailable: true,
    photos: [], modifierIds: [],
  });

  const openEdit = (d: Dish) => setForm({
    id: d.id,
    categoryId: d.categoryId,
    sectionId: d.sectionId ?? '',
    name: d.name,
    description: d.description ?? '',
    priceRub: d.price / 100,
    costRub: d.costPrice == null ? '' : String(d.costPrice / 100),
    isAvailable: d.isAvailable,
    photos: d.photos?.length ? d.photos : d.photoUrl ? [d.photoUrl] : [],
    modifierIds: d.modifiers.map((m) => m.id),
  });

  return (
    <div>
      <div className="flex flex-wrap gap-2 sm:gap-3 mb-3">
        <select className="input w-full sm:w-56" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          <option value="">Все категории</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input className="input flex-1 min-w-[180px]" placeholder="Поиск блюда…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn-primary" onClick={openNew}><Plus size={16} /> <span className="hidden sm:inline">Блюдо</span></button>
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? <Spinner /> : (
          <table className="w-full min-w-[780px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Название</th>
                <th className="text-left px-4 py-3">Категория</th>
                <th className="text-left px-4 py-3">Цена</th>
                <th className="text-left px-4 py-3">Закупка</th>
                <th className="text-left px-4 py-3">Раздел</th>
                <th className="text-left px-4 py-3">Статус</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {dishes?.map((d) => {
                const cover = d.photos?.[0] ?? d.photoUrl ?? null;
                const extra = Math.max(0, (d.photos?.length ?? 0) - 1);
                return (
                <tr key={d.id} className="table-row">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="relative w-10 h-10 shrink-0 rounded-md overflow-hidden bg-slate-100 flex items-center justify-center">
                        {cover ? (
                          <img
                            src={asAbsoluteUrl(cover) ?? cover}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                          />
                        ) : (
                          <span className="text-base text-slate-300">🍽</span>
                        )}
                        {extra > 0 && (
                          <span className="absolute bottom-0 right-0 bg-slate-900/80 text-white text-[9px] leading-none px-1 py-0.5 rounded-tl">
                            +{extra}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{d.name}</div>
                        {d.description && <div className="text-xs text-slate-400 line-clamp-1">{d.description}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">{categories.find((c) => c.id === d.categoryId)?.name ?? '—'}</td>
                  <td className="px-4 py-3">{formatRub(d.price)}</td>
                  <td className="px-4 py-3">
                    {/* Себестоимость: ручная цена, тех-карта или ничего. Здесь же
                        вход в тех-карту — иначе владелец о ней не узнаёт: она
                        живёт в Складе, а блюда создают тут. */}
                    {(() => {
                      const cardCost = techCardCostByDish.get(d.id);
                      if (d.costPrice != null) {
                        const m = marginPct(d.price, d.costPrice);
                        return (
                          <div>
                            <div>{formatRub(d.costPrice)}</div>
                            <div className="text-xs text-slate-400">вручную</div>
                            {m != null && (
                              <div className={`text-xs ${m < 0 ? 'text-rose-500' : 'text-slate-400'}`}>
                                маржа {m}%
                              </div>
                            )}
                          </div>
                        );
                      }
                      if (cardCost != null) {
                        const m = marginPct(d.price, cardCost);
                        return (
                          <div>
                            <div>{formatRub(cardCost)}</div>
                            <button
                              type="button"
                              className="text-xs text-indigo-600 hover:underline"
                              onClick={() => setTechCardFor(d)}
                            >
                              по тех-карте
                            </button>
                            {m != null && (
                              <div className={`text-xs ${m < 0 ? 'text-rose-500' : 'text-slate-400'}`}>
                                маржа {m}%
                              </div>
                            )}
                          </div>
                        );
                      }
                      return (
                        <button
                          type="button"
                          className="text-xs text-indigo-600 hover:underline"
                          onClick={() => setTechCardFor(d)}
                        >
                          собрать тех-карту
                        </button>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      // Название берём из справочника; если раздел не задан —
                      // показываем legacy-значение, чтобы столбец не пустовал
                      // у блюд, созданных до появления справочника.
                      const name = sections.find((s) => s.id === d.sectionId)?.name;
                      if (name) return <Badge tone="info">{name}</Badge>;
                      if (d.section === 'KITCHEN') return <Badge tone="info">Кухня</Badge>;
                      if (d.section === 'BAR') return <Badge tone="warning">Бар</Badge>;
                      return <Badge>—</Badge>;
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    {d.isAvailable ? <Badge tone="success">Доступно</Badge> : <Badge tone="slate">Выкл</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button className="btn-ghost" onClick={() => openEdit(d)}><Edit size={16} /></button>
                    <button className="btn-ghost text-red-600" onClick={() => { if (confirm('Удалить блюдо?')) delMut.mutate(d.id); }}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
                );
              })}
              {dishes?.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-400 py-8">Блюд не найдено</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? 'Редактировать блюдо' : 'Новое блюдо'}
        size="lg"
        footer={
          <>
            <button className="btn-outline" onClick={() => setForm(null)}>Отмена</button>
            <button className="btn-primary" onClick={() => saveMut.mutate()}>Сохранить</button>
          </>
        }
      >
        {form && (
          <div className="grid gap-3">
            <div>
              <label className="label">Название</label>
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="label">Описание</label>
              <textarea className="input" rows={3} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="label">Цена (₽)</label>
                <NumberInput className="input" value={form.priceRub}
                  onChange={(n) => setForm({ ...form, priceRub: n })} />
              </div>
              <div>
                <label className="label">Закупочная цена (₽)</label>
                <input
                  className="input"
                  inputMode="decimal"
                  placeholder="не задана"
                  value={form.costRub}
                  onChange={(e) => setForm({ ...form, costRub: e.target.value })}
                />
                <div className="text-xs text-slate-400 mt-1">
                  {(() => {
                    const cost = form.costRub.trim();
                    if (cost === '') return 'Возьмём из тех-карты склада, если она есть';
                    const m = marginPct(form.priceRub, Number(cost.replace(',', '.')));
                    if (m == null || Number.isNaN(m)) return 'Себестоимость порции';
                    return `Маржа ${m}% · прибыль ${(form.priceRub - Number(cost.replace(',', '.'))).toLocaleString('ru-RU')} ₽`;
                  })()}
                </div>
              </div>
              <div>
                <label className="label">Раздел</label>
                <select className="input" value={form.sectionId}
                  onChange={(e) => setForm({ ...form, sectionId: e.target.value })}>
                  <option value="">— без раздела</option>
                  {sections.filter((s) => s.isActive || s.id === form.sectionId).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <div className="text-xs text-slate-400 mt-1">
                  Определяет принтер: касса печатает раздел на привязанный к нему принтер.
                  Список правится на вкладке «Разделы».
                </div>
              </div>
              <div>
                <label className="label">Категория</label>
                <select className="input" value={form.categoryId}
                  onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="label">Фото блюда</label>
              <PhotosField
                kind="dish"
                max={MAX_PHOTOS}
                value={form.photos}
                onChange={(photos) => setForm({ ...form, photos })}
              />
            </div>
            <div>
              <label className="label">Модификаторы</label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-auto">
                {mods.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.modifierIds.includes(m.id)}
                      onChange={(e) => {
                        if (e.target.checked) setForm({ ...form, modifierIds: [...form.modifierIds, m.id] });
                        else setForm({ ...form, modifierIds: form.modifierIds.filter((x) => x !== m.id) });
                      }}
                    />
                    <span>{m.name} <span className="text-slate-400">+{formatRub(m.price)}</span></span>
                  </label>
                ))}
                {mods.length === 0 && <div className="text-xs text-slate-400 col-span-2">Модификаторов нет.</div>}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isAvailable}
                onChange={(e) => setForm({ ...form, isAvailable: e.target.checked })} />
              Доступно для заказа
            </label>
          </div>
        )}
      </Modal>

      {techCardFor && (
        <TechCardEditor
          dishId={techCardFor.id}
          dishName={techCardFor.name}
          dishPrice={techCardFor.price}
          onClose={() => setTechCardFor(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['admin/stock/tech-cards'] });
            qc.invalidateQueries({ queryKey: ['admin/dishes'] });
          }}
        />
      )}
    </div>
  );
}

// ─── Modifiers ──────────────────────────────
function ModifiersTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState<{ id?: string; name: string; priceRub: number; isActive: boolean } | null>(null);
  const [techCardFor, setTechCardFor] = useState<Modifier | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['admin/mods'], queryFn: listModifiers });

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!form) return;
      const payload = { name: form.name, price: Math.round(form.priceRub * 100), isActive: form.isActive };
      if (form.id) return updateModifier(form.id, payload);
      return createModifier(payload);
    },
    onSuccess: () => {
      toast.success('Сохранено');
      setForm(null);
      qc.invalidateQueries({ queryKey: ['admin/mods'] });
    },
  });

  const delMut = useMutation({
    mutationFn: deleteModifier,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin/mods'] }),
  });

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button className="btn-primary" onClick={() => setForm({ name: '', priceRub: 0, isActive: true })}>
          <Sparkles size={16} /> Модификатор
        </button>
      </div>
      <div className="card overflow-x-auto">
        {isLoading ? <Spinner /> : (
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Название</th>
                <th className="text-left px-4 py-3">Цена</th>
                <th className="text-left px-4 py-3">Тех-карта</th>
                <th className="text-left px-4 py-3">Статус</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data?.map((m: Modifier) => (
                <tr key={m.id} className="table-row">
                  <td className="px-4 py-3 font-medium">{m.name}</td>
                  <td className="px-4 py-3">{formatRub(m.price)}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:underline"
                      onClick={() => setTechCardFor(m)}
                      title="Что списывается со склада при выборе модификатора"
                    >
                      <ChefHat size={14} />
                      {(m.techCardItems ?? 0) > 0
                        ? `${formatRub(m.costPerUnit ?? 0)} · ${m.techCardItems} ингр.`
                        : 'Заполнить'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    {m.isActive ? <Badge tone="success">Активен</Badge> : <Badge tone="slate">Выкл</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button className="btn-ghost" onClick={() => setForm({ id: m.id, name: m.name, priceRub: m.price / 100, isActive: m.isActive })}>
                      <Edit size={16} />
                    </button>
                    <button className="btn-ghost text-red-600" onClick={() => { if (confirm('Удалить?')) delMut.mutate(m.id); }}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {data?.length === 0 && (
                <tr><td colSpan={5} className="text-center text-slate-400 py-8">Модификаторов нет</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? 'Редактировать модификатор' : 'Новый модификатор'}
        footer={
          <>
            <button className="btn-outline" onClick={() => setForm(null)}>Отмена</button>
            <button className="btn-primary" onClick={() => saveMut.mutate()}>Сохранить</button>
          </>
        }
      >
        {form && (
          <div className="grid gap-3">
            <div>
              <label className="label">Название</label>
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="label">Цена (₽)</label>
              <NumberInput className="input" value={form.priceRub}
                onChange={(n) => setForm({ ...form, priceRub: n })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              Активен
            </label>
          </div>
        )}
      </Modal>
      {techCardFor && (
        <ModifierTechCardModal
          modifierId={techCardFor.id}
          modifierName={techCardFor.name}
          modifierPrice={techCardFor.price}
          onClose={() => setTechCardFor(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['admin/mods'] })}
        />
      )}
    </div>
  );
}
